import React, { useState, useEffect } from "react";
import {
  Home,
  Bed,
  Bath,
  Car,
  Square,
  FileText,
  List as ListIcon,
  User,
  Phone,
  ChevronLeft,
} from "lucide-react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

// Mock property data (for fallback)
const mockProperty = {
  id: 1,
  title: "Modern 3-Bedroom Apartment",
  address: "123 Main Street, Springfield, IL",
  price: "$250,000.00",
  status: "For Sale",
  beds: 3,
  baths: 2,
  parking: 1,
  area: 1400.5,
  images: [
    "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80",
  ],
  description:
    "Spacious and modern apartment in a prime location with great amenities. Enjoy the open-plan living, large balcony, and access to the building's gym and pool.",
  features: ["Balcony", "Elevator", "Gym Access"],
  owner_name: "Jane Doe",
  contact: "+1-555-123-4567",
};

const ViewProperty = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImage, setCurrentImage] = useState(0);
  const { isProperty } = useLocation().state || { isProperty: false };
  const brandColors = {
    primary: "#575955",
    secondary: "#55C5D0",
  };
  // console.log(property);
  useEffect(() => {
    // TODO: Replace with real API call
    setLoading(true);
    setError(null);
    setTimeout(() => {
      // Simulate API fetch
      if (id === "1") {
        setProperty(mockProperty);
        setLoading(false);
      } else {
        setError("Property not found");
        setLoading(false);
      }
    }, 700);
  }, [id]);

  const notFoundImg =
    "https://atozpropertysolutionsltd.com/property-not-found.svg";

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow p-6 mt-8 flex items-center justify-center h-96">
        <span className="text-gray-400 text-lg">Loading property...</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow p-6 mt-8 flex flex-col items-center justify-center h-96">
        <img
          src={notFoundImg}
          alt="Property not found"
          className="w-40 h-40 mb-4"
        />
        <span className="text-red-500 text-lg mb-2">Property not found</span>
        <p className="text-gray-500 mb-4">
          The property you are looking for does not exist or has been removed.
        </p>
        <button
          className="flex items-center text-gray-600 hover:text-blue-500"
          onClick={() => navigate(-1)}
        >
          <ChevronLeft className="w-5 h-5 mr-1" /> Back
        </button>
      </div>
    );
  }
  return (
    <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow p-6 mt-8">
      {/* Back Button */}
      <button
        className="flex items-center text-gray-600 hover:text-blue-500 mb-4"
        onClick={() => navigate(-1)}
      >
        <ChevronLeft className="w-5 h-5 mr-1" /> Back
      </button>

      {/* Image Carousel */}
      <div className="relative mb-6">
        <img
          src={property.images[currentImage]}
          alt={property.title}
          className="w-full h-72 object-cover rounded-xl border"
        />
        {property?.images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-2">
            {property.images.map((_, idx) => (
              <button
                key={idx}
                className={`w-3 h-3 rounded-full border-2 ${
                  idx === currentImage
                    ? "bg-blue-500 border-blue-500"
                    : "bg-white border-gray-300"
                }`}
                onClick={() => setCurrentImage(idx)}
                aria-label={`Go to image ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Title, Address, Price, Status */}
      <div className="mb-4">
        <h2 className="text-2xl font-semibold mb-1 flex items-center gap-2">
          <Home className="w-6 h-6 text-blue-500" /> {property.title}
        </h2>
        <div className="flex items-center text-gray-500 mb-1">
          <span className="mr-2">{property.address}</span>
        </div>
        <div className="flex items-center gap-4">
          <span
            className="text-xl font-bold"
            style={{ color: brandColors.primary }}
          >
            {property.price}
          </span>
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
            {property.status}
          </span>
        </div>
      </div>

      {/* Property Features (Beds, Baths, Parking, Area) */}
      <div className="flex items-center gap-6 mb-4">
        <div className="flex items-center text-gray-600">
          <Bed className="w-5 h-5 mr-1" />
          <span>{property.beds} Beds</span>
        </div>
        <div className="flex items-center text-gray-600">
          <Bath className="w-5 h-5 mr-1" />
          <span>{property.baths} Baths</span>
        </div>
        <div className="flex items-center text-gray-600">
          <Car className="w-5 h-5 mr-1" />
          <span>{property.parking} Parking</span>
        </div>
        <div className="flex items-center text-gray-600">
          <Square className="w-5 h-5 mr-1" />
          <span>{property.area} sqft</span>
        </div>
      </div>

      {/* Description */}
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1 flex items-center gap-1">
          <FileText className="w-4 h-4" /> Description
        </label>
        <p className="text-gray-700 text-base bg-gray-50 rounded p-3">
          {property.description}
        </p>
      </div>

      {/* Features */}
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1 flex items-center gap-1">
          <ListIcon className="w-4 h-4" /> Features
        </label>
        <div className="flex flex-wrap gap-2">
          {property.features.map((feature, idx) => (
            <span
              key={idx}
              className="px-3 py-1 rounded-full text-xs bg-gray-100 text-gray-700"
            >
              {feature}
            </span>
          ))}
        </div>
      </div>

      {/* Owner & Contact */}
      <div className="mb-2 flex items-center gap-6">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-gray-400" />
          <span className="font-medium">{property.owner_name}</span>
        </div>
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-gray-400" />
          <span className="font-medium">{property.contact}</span>
        </div>
      </div>
    </div>
  );
};

export default ViewProperty;
